import type { ServerConnection } from "@/context/server"
import { authTokenFromCredentials } from "./server"
import { nativeSessionActive } from "./native-session-wire"

export type ServerProtocol = "v1" | "v2"

export async function hasNativeApi(server: ServerConnection.HttpBase, fetch: typeof globalThis.fetch) {
  const response = await fetch(new URL(server.url.replace(/\/+$/, "") + "/api/health"), {
    credentials: "same-origin", headers: headers(server), signal: AbortSignal.timeout(5_000),
  })
  if (response.status === 404) return false
  if (!response.ok) throw new Error(`Native health failed: ${response.status}`)
  const data = await response.json()
  if (typeof data?.pid === "number") return true
  if (data?.healthy !== true) throw new Error("Invalid native health response")
  // Both transitional legacy and pinned native runtimes omit pid. Confirm the
  // native read model before enabling its stream; session pins still own writes.
  const active = await fetch(new URL(server.url.replace(/\/+$/, "") + "/api/session/active"), {
    credentials: "same-origin", headers: headers(server), signal: AbortSignal.timeout(5_000),
  })
  if (active.status === 404) return false
  if (!active.ok) throw new Error(`Native activity failed: ${active.status}`)
  nativeSessionActive((await active.json()).data)
  return true
}

function headers(server: ServerConnection.HttpBase) {
  if (!server.password) return
  return {
    Authorization: `Basic ${authTokenFromCredentials({ username: server.username, password: server.password })}`,
  }
}

async function probe(server: ServerConnection.HttpBase, fetch: typeof globalThis.fetch, path: string) {
  const response = await fetch(new URL(server.url.replace(/\/+$/, "") + path), {
    headers: headers(server),
    signal: AbortSignal.timeout(5_000),
  })
  if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return
  const value: unknown = await response.json()
  if (!value || typeof value !== "object") return
  return value
}

export async function detectServerProtocol(
  server: ServerConnection.HttpBase,
  fetch: typeof globalThis.fetch,
): Promise<ServerProtocol> {
  const legacy = await probe(server, fetch, "/global/health").catch(() => undefined)
  if (legacy && "healthy" in legacy && legacy.healthy === true) return "v1"

  const current = await probe(server, fetch, "/api/health").catch(() => undefined)
  if (current && "pid" in current && typeof current.pid === "number") return "v2"
  if (current && "healthy" in current && current.healthy === true) return "v1"
  return "v2"
}
